/**
 * The numeric enums the orders API returns, and how they read on screen.
 *
 * The original scattered these across `utils/functions.js` as three `switch`
 * statements returning translation keys, with the badge colour decided
 * separately at each render site — so the same status was green in one table
 * and grey in another. Status, label and tone are declared together here.
 */

export type BadgeTone =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning';

interface StatusMeta {
  labelKey: string;
  tone: BadgeTone;
}

/** Verbatim from `mapOrderStatus`; the gaps in the numbering are the API's. */
export const ORDER_STATUS: Record<number, StatusMeta> = {
  0: { labelKey: 'orders.status.placed', tone: 'default' },
  2: { labelKey: 'orders.status.deliveryAssigned', tone: 'warning' },
  3: { labelKey: 'orders.status.accepted', tone: 'warning' },
  4: { labelKey: 'orders.status.ready', tone: 'warning' },
  5: { labelKey: 'orders.status.deliveryStarted', tone: 'warning' },
  6: { labelKey: 'orders.status.customerPickedUp', tone: 'success' },
  7: { labelKey: 'orders.status.delivered', tone: 'success' },
  8: { labelKey: 'orders.status.storeCancelled', tone: 'destructive' },
  9: { labelKey: 'orders.status.userCancelled', tone: 'destructive' },
  10: { labelKey: 'orders.status.declined', tone: 'destructive' },
  16: { labelKey: 'orders.status.pickingStarted', tone: 'warning' },
  17: { labelKey: 'orders.status.pickingFinished', tone: 'warning' },
  18: { labelKey: 'orders.status.packingStarted', tone: 'warning' },
  20: { labelKey: 'orders.status.readyForPickup', tone: 'warning' },
  21: { labelKey: 'orders.status.pendingDeliveryAssignment', tone: 'warning' },
  27: { labelKey: 'orders.status.pendingPaypalPayment', tone: 'secondary' },
  30: { labelKey: 'orders.status.pendingPayment', tone: 'secondary' },
  32: { labelKey: 'orders.status.underReview', tone: 'secondary' },
  33: { labelKey: 'orders.status.ivuControlPending', tone: 'secondary' },
};

export function statusMeta(status: number | null | undefined): StatusMeta {
  if (status === null || status === undefined) {
    return { labelKey: 'orders.status.unknown', tone: 'outline' };
  }
  return (
    ORDER_STATUS[status] ?? {
      labelKey: 'orders.status.unknown',
      tone: 'outline',
    }
  );
}

/** A freshly placed order is worth calling out in the list. */
export const STATUS_PLACED = 0;

export const ORDER_TYPE: Record<number, string> = {
  0: 'orders.type.pickup',
  1: 'orders.type.delivery',
  5: 'orders.type.inStore',
  6: 'orders.type.deliPickup',
  7: 'orders.type.deliDelivery',
};

export function typeLabelKey(type: number | null | undefined): string {
  if (type === null || type === undefined) return 'orders.type.unknown';
  return ORDER_TYPE[type] ?? 'orders.type.unknown';
}

/**
 * Payment methods. These are proper nouns rather than translatable phrases —
 * "Stripe" is "Stripe" in every language — so they are plain strings, unlike
 * the status and type maps.
 */
export const PAYMENT_TYPE: Record<number, string> = {
  0: 'Stripe',
  1: 'ATH Móvil',
  2: 'Cash',
  3: 'Other',
  4: 'EBT',
  5: 'PayPal',
  6: 'Cheque',
  7: 'ATH',
  8: 'Vanilla Card',
  9: 'Gift Card',
  10: 'MCS',
  11: 'MMM',
  12: 'Accepta',
};

export function paymentLabel(type: number | null | undefined): string {
  if (type === null || type === undefined) return '—';
  return PAYMENT_TYPE[type] ?? '—';
}

/**
 * Filter tags. The API takes them as a single space-separated string, so an
 * empty tag means "any" and simply contributes nothing.
 */
export const STATUS_FILTERS = [
  { tag: '', labelKey: 'orders.filters.any' },
  { tag: 'ACTIVE', labelKey: 'orders.filters.active' },
  { tag: 'PLACED', labelKey: 'orders.status.placed' },
  { tag: 'INPROCESS', labelKey: 'orders.filters.processing' },
  { tag: 'PICKING', labelKey: 'orders.filters.picking' },
  { tag: 'PACKING', labelKey: 'orders.filters.packing' },
  { tag: 'WAITING', labelKey: 'orders.filters.waiting' },
  { tag: 'INTRANSIT', labelKey: 'orders.filters.inTransit' },
  { tag: 'DISPATCHED', labelKey: 'orders.filters.completed' },
  { tag: 'CANCELLED', labelKey: 'orders.filters.cancelled' },
  { tag: 'PAYMENTFAILED', labelKey: 'orders.filters.paymentFailed' },
  { tag: 'PENDING', labelKey: 'orders.filters.pending' },
] as const;

export const TIME_FILTERS = [
  { tag: '', labelKey: 'orders.filters.any' },
  { tag: 'PAST', labelKey: 'orders.filters.past' },
  { tag: 'YESTERDAY', labelKey: 'orders.filters.yesterday' },
  { tag: 'TODAY', labelKey: 'orders.filters.today' },
  { tag: 'TOMORROW', labelKey: 'orders.filters.tomorrow' },
  { tag: 'FUTURE', labelKey: 'orders.filters.future' },
] as const;

export const TYPE_FILTERS = [
  { tag: '', labelKey: 'orders.filters.any' },
  { tag: 'PICKUP', labelKey: 'orders.type.pickup' },
  { tag: 'DELIVERY', labelKey: 'orders.type.delivery' },
  { tag: 'INSTORE', labelKey: 'orders.type.inStore' },
] as const;
